'use client';

/**
 * CRM ONE-FOCUS DECK (Control Room v6 §6 — Boss named CRM first).
 * Cards = real leads (/api/hub/crm/all/leads). Focus: WHY = identity + full
 * message + timeline (/leads/[id]), PREVIEW = mail threads for the lead's site
 * (/mail), DECIDE = REPLY IN HUB + OPEN CRM deep-links (status transitions
 * live in the hub — classic parity, no invented API).
 */
import { useCallback, useEffect, useState } from 'react';
import { OneFocusDeck, stColor, stLabel, bigVerb, CY, type OFItem, type OFAdapter } from './OneFocus';
import { mono, display } from '@/future/primitives';

const HUB_URL = 'https://app.anydomain.ca';
const age = (iso: string) => {
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d === 0 ? 'TODAY' : d === 1 ? '1 DAY' : d < 30 ? `${d} DAYS` : `${Math.floor(d / 30)} MO`;
};

function Why({ item }: { item: OFItem }) {
  const l = item.raw;
  const [timeline, setTimeline] = useState<any[]>([]);
  useEffect(() => {
    fetch(`/api/hub/crm/${l.site}/leads/${l.id}`).then(r => r.json())
      .then(d => setTimeline(d.timeline || [])).catch(() => { });
  }, [l.site, l.id]);
  return (
    <>
      {[['EMAIL', l.email], ['PHONE', l.phone], ['SITE', l.site], ['SOURCE', l.source], ['CREATED', new Date(l.created_at).toLocaleString()]].filter(([, v]) => v).map(([k, v]) => (
        <div key={k as string} className="flex justify-between" style={{ padding: '4px 0', gap: 10 }}>
          <span style={{ ...mono(15, '.16em'), color: '#7fa9bd', flexShrink: 0 }}>{k}</span>
          <span style={{ ...mono(16, '.04em'), color: '#d7f2ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v as string}</span>
        </div>
      ))}
      <div style={{ marginTop: 14, borderTop: '1px solid rgba(125,232,255,.12)', paddingTop: 10 }}>
        <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 8 }}>MESSAGE</div>
        <div style={{ ...display(24, 300), color: 'var(--uif-t-body)', lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>{l.message || '(no message)'}</div>
      </div>
      {timeline.length > 0 && (
        <div style={{ marginTop: 14, borderTop: '1px solid rgba(125,232,255,.12)', paddingTop: 10 }}>
          <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 8 }}>TIMELINE · {timeline.length}</div>
          {timeline.slice(0, 8).map((t: any, i: number) => (
            <div key={i} style={{ padding: '5px 0', borderLeft: `2px solid ${CY}33`, paddingLeft: 10, marginBottom: 4 }}>
              <div style={{ ...mono(13, '.16em'), color: '#56788c' }}>{(t.type || t.event || 'EVENT').toUpperCase()} · {t.created_at ? new Date(t.created_at).toLocaleDateString() : ''}</div>
              {(t.note || t.detail || t.message) && <div style={{ ...display(21, 300), color: '#9fb8c6', marginTop: 2 }}>{String(t.note || t.detail || t.message).slice(0, 140)}</div>}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function Preview({ item }: { item: OFItem }) {
  const l = item.raw;
  const [threads, setThreads] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    fetch(`/api/hub/crm/${l.site}/mail?days=90`).then(r => r.json())
      .then(d => {
        const all = d.threads || [];
        const mine = all.filter((t: any) => (t.from || '').includes(l.email) || (t.participants || []).some((p: string) => p.includes(l.email)) || (t.subject || '').toLowerCase().includes((l.name || '').toLowerCase()));
        setThreads(mine.length ? mine : all.slice(0, 5)); setLoaded(true);
      }).catch(() => setLoaded(true));
  }, [l.site, l.email, l.name]);
  return (
    <div>
      <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 10 }}>MAIL · {l.site?.toUpperCase()} · LAST 90 DAYS</div>
      {threads.map((t: any, i: number) => (
        <div key={i} style={{ marginBottom: 10, padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(125,232,255,.14)', background: 'rgba(8,18,28,.4)' }}>
          <div className="flex justify-between" style={{ gap: 10 }}>
            <span style={{ ...display(23, 400), color: '#e9f9ff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.subject || '(no subject)'}</span>
            <span style={{ ...mono(14), color: '#56788c', flexShrink: 0 }}>{t.count || t.messages?.length || 1} MSG</span>
          </div>
          <div style={{ ...mono(14, '.08em'), color: '#7fa9bd', marginTop: 3 }}>{t.from || t.lastFrom || ''}</div>
          {(t.snippet || t.preview) && <div style={{ ...display(21, 300), color: '#9fb8c6', marginTop: 4, lineHeight: 1.5, maxHeight: 46, overflow: 'hidden' }}>{t.snippet || t.preview}</div>}
        </div>
      ))}
      {loaded && !threads.length && <div style={{ ...display(23, 300), color: '#56788c' }}>No mail threads for this site yet — the reply starts one.</div>}
    </div>
  );
}

function Decide({ item }: { item: OFItem }) {
  const l = item.raw;
  const FLOW = ['new', 'contacted', 'quoted', 'won', 'lost'];
  const cur = FLOW.indexOf((l.status || 'new').toLowerCase());
  return (
    <>
      <div style={{ ...mono(14, '.24em'), color: '#49677a', marginBottom: 8 }}>PIPELINE</div>
      <div className="flex flex-col" style={{ gap: 6, marginBottom: 16 }}>
        {FLOW.map((s, i) => (
          <div key={s} className="flex items-center" style={{ gap: 10, opacity: i === cur ? 1 : .45 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: i === cur ? stColor(s) : 'rgba(125,232,255,.15)', boxShadow: i === cur ? `0 0 10px ${stColor(s)}` : 'none', flexShrink: 0 }} />
            <span style={{ ...mono(16, '.2em'), color: i === cur ? stColor(s) : '#56788c' }}>{stLabel(s)}</span>
            {i === cur && <span style={{ ...mono(13, '.14em'), color: '#49677a' }}>· CURRENT</span>}
          </div>
        ))}
      </div>
      {/* status transitions + reply live in the hub — Beast deep-links (classic parity) */}
      <a href={`${HUB_URL}/crm/${l.site}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
        <div style={bigVerb()}>✉ REPLY IN HUB</div>
      </a>
      <a href={`${HUB_URL}/crm/${l.site}`} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}>
        <div style={{ width: '100%', padding: '10px 0', marginTop: 10, borderRadius: 12, background: 'transparent', border: '1px solid rgba(125,232,255,.3)', color: CY, ...mono(16, '.26em'), textAlign: 'center', cursor: 'pointer' }}>
          OPEN CRM · {String(l.site || '').toUpperCase()} ↗
        </div>
      </a>
      <div style={{ ...mono(13, '.12em'), color: '#3d4654', marginTop: 10, textAlign: 'center' }}>STATUS TRANSITIONS WRITE THROUGH THE HUB</div>
    </>
  );
}

export function CrmDeckSurface({ sites, scopeName }: { sites?: string[]; scopeName?: string } = {}) {
  const [items, setItems] = useState<OFItem[]>([]);
  const [loaded, setLoaded] = useState(false);
  const load = useCallback(() => {
    fetch('/api/hub/crm/all/leads?limit=500').then(r => r.json()).then(d => {
      /* project scoping (Boss 2026-07-27 'CRM in Private Charters shows all projects') —
         hub API has no per-project filter, so filter client-side by site domain */
      const norm = (s: string) => String(s || '').toLowerCase().replace(/^www\./, '');
      const scope = sites?.map(norm);
      const leads = (d.leads || []).filter((l: any) => !scope || scope.includes(norm(l.site)));
      setItems(leads.map((l: any): OFItem => ({
        /* id must be site-scoped — per-site CRMs reuse numeric lead ids; bare l.id gave
           duplicate React keys → tripled ghost cards (Boss 2026-07-10 'CRM got bugged') */
        id: `${l.site || 'x'}-${l.id}`, biz: l.site || 'unknown', title: l.name || l.email || 'Unknown',
        type: 'LEAD', status: (l.status || 'new').toLowerCase(),
        kpis: [{ l: 'AGE', v: age(l.created_at) }, ...(l.phone ? [{ l: 'PHONE', v: '✓' }] : []), { l: 'SOURCE', v: (l.source || 'FORM').toUpperCase().slice(0, 10) }],
        taste: l.message ? String(l.message).slice(0, 220) : undefined,
        foot: l.email, raw: l,
      })));
      setLoaded(true);
    }).catch(() => setLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites?.join(',')]);
  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, [load]);

  const adapter: OFAdapter = {
    kind: 'crm',
    strap: (its) => `ONE FOCUS · ${its.length} LEADS · ${scopeName ? scopeName.toUpperCase() + ' · ' : ''}HUB LIVE`,
    decidable: (it) => it.status === 'new',
    Why, Preview, Decide,
  };
  return <OneFocusDeck items={items} loaded={loaded} adapter={adapter} refresh={load} />;
}
